import { enum_param, page_param, string_param, use_url_state } from '@/hooks/use-url-state';
import { DateRangeFilter, VendorFilter } from '@/components/list-filters';
import { Loader2 } from 'lucide-react';
import { use_auth_store } from '@/lib/auth-store';
import { DashboardLayout } from '@/components/dashboard-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { use_order_list } from '@/features/orders/hooks';
import { ORDER_STATUS_OPTIONS, type AdminOrderFilters, type OrderStatus } from '@/features/orders/types';

const PAGE_SIZE = 20;

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

export function OrderListPage() {
  const user = use_auth_store((s) => s.user);
  const [status, set_status] = use_url_state('status', enum_param(ORDER_STATUS_OPTIONS));
  const [page, set_page] = use_url_state('page', page_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [vendor_id, set_vendor_id] = use_url_state('vendor_profile_id', string_param);

  const filters: AdminOrderFilters = {
    page,
    page_size: PAGE_SIZE,
    status: status || undefined,
    from: from || undefined,
    to: to || undefined,
    vendor_profile_id: vendor_id || undefined,
  };
  const { data, isLoading, isFetching, error } = use_order_list(filters);

  const total = data?.total ?? 0;
  const total_pages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 1;

  return (
    <DashboardLayout title="Orders" subtitle={`Signed in as ${user?.email ?? 'admin'}`}>
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-muted-foreground" htmlFor="filter-status">
              Status
            </label>
            <select
              id="filter-status"
              data-testid="filter-status"
              value={status}
              onChange={(e) => {
                set_status(e.target.value as OrderStatus | '');
                set_page(1);
              }}
              className="clay-select"
            >
              <option value="">All</option>
              {ORDER_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
          <DateRangeFilter
            from={from}
            to={to}
            on_change={(next) => {
              set_from(next.from);
              set_to(next.to);
              set_page(1);
            }}
          />
          <VendorFilter
            vendor_id={vendor_id}
            on_change={(id) => {
              set_vendor_id(id);
              set_page(1);
            }}
          />

        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="p-6 text-sm text-destructive" data-testid="order-list-error">
              Failed to load orders. {error instanceof Error ? error.message : ''}
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center p-12 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !data || data.items.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground" data-testid="order-list-empty">
              No orders match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="order-list-table">
                <thead className="bg-secondary/40 text-left text-xs font-semibold uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Placed</th>
                    <th className="px-4 py-3">Vendor</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Fulfilment</th>
                    <th className="px-4 py-3">Items</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.items.map((o) => (
                    <tr key={o.id} data-testid={`order-row-${o.id}`} className="hover:bg-secondary/30">
                      <td className="px-4 py-3 text-muted-foreground">{fmt(o.created_at)}</td>
                      <td className="px-4 py-3 font-medium">{o.vendor_business_name ?? '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{o.customer_name ?? '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{o.fulfillment_type}</td>
                      <td className="px-4 py-3 text-muted-foreground">{o.items.reduce((n, it) => n + it.quantity, 0)}</td>
                      <td className="px-4 py-3 tabular-nums">₹{o.total_inr}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold">{o.status.replace(/_/g, ' ')}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {data && data.total > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground" data-testid="order-list-pagination">
          <span>
            Showing page {data.page} of {total_pages} ({data.total} total)
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || isFetching} onClick={() => set_page((p) => Math.max(1, p - 1))} data-testid="page-prev">
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= total_pages || isFetching} onClick={() => set_page((p) => p + 1)} data-testid="page-next">
              Next
            </Button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
